// src/App.js
import React, { Suspense, lazy, useMemo, useCallback, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import "./App.css";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useAuth } from "./contexts/AuthContext";
import { useLogo } from "./hooks/useLogo";
import LoadingSpinner from "./components/LoadingSpinner";
import SectionErrorBoundary from "./components/SectionErrorBoundary";
import { logDebug, logError } from "./lib/logger";
import { useSessionTimeout } from "./hooks/useSessionTimeout";
import { OverflowTooltipProvider } from "./components/OverflowTooltipProvider";
import { LEGACY_TASK_PATHS, TASK_HUB_TABS, normalizeTaskHubTab, taskHubPath } from "./lib/taskHubTabs";
import { useTaskHubCounts } from "./hooks/useTaskHubCounts";

// Lazy load page components for code splitting
const Dashboard = lazy(() =>
  import("./pages/Dashboard").then((module) => ({ default: module.Dashboard }))
);
const CommandCenter = lazy(() =>
  import("./pages/CommandCenter").then((module) => ({ default: module.CommandCenter }))
);
const Settings = lazy(() =>
  import("./pages/Settings").then((module) => ({ default: module.Settings }))
);
const SettingsTest = lazy(() =>
  import("./pages/SettingsTest").then((module) => ({ default: module.SettingsTest }))
);
const DailySales = lazy(() =>
  import("./pages/DailySales").then((module) => ({ default: module.DailySales }))
);
const Inventory = lazy(() =>
  import("./pages/Inventory").then((module) => ({ default: module.Inventory }))
);
const FloorExposure = lazy(() =>
  import("./pages/FloorExposure").then((module) => ({ default: module.FloorExposure }))
);
const RestockingReports = lazy(() =>
  import("./pages/RestockingReports").then((module) => ({ default: module.RestockingReports }))
);
const SupplyOrders = lazy(() =>
  import("./pages/SupplyOrders").then((module) => ({ default: module.SupplyOrders }))
);
const DealPosters = lazy(() =>
  import("./pages/DealPosters").then((module) => ({ default: module.DealPosters }))
);
const FlowhubBrandImages = lazy(() =>
  import("./pages/FlowhubBrandImages").then((module) => ({ default: module.FlowhubBrandImages }))
);
const FlowhubDealGraphics = lazy(() =>
  import("./pages/FlowhubDealGraphics").then((module) => ({ default: module.FlowhubDealGraphics }))
);
const BrandLogos = lazy(() =>
  import("./pages/BrandLogos").then((module) => ({ default: module.BrandLogos }))
);
const BrandAssetReview = lazy(() =>
  import("./pages/BrandAssetReview").then((module) => ({ default: module.BrandAssetReview }))
);
const BuyerTasks = lazy(() =>
  import("./pages/BuyerTasks").then((module) => ({ default: module.BuyerTasks }))
);
const InventoryCleanupTasks = lazy(() =>
  import("./pages/InventoryCleanupTasks").then((module) => ({
    default: module.InventoryCleanupTasks,
  }))
);
const StaleInventoryTasks = lazy(() =>
  import("./pages/StaleInventoryTasks").then((module) => ({
    default: module.StaleInventoryTasks,
  }))
);
const MenuGapTasks = lazy(() =>
  import("./pages/MenuGapTasks").then((module) => ({
    default: module.MenuGapTasks,
  }))
);
const WeeklyReports = lazy(() =>
  import("./pages/WeeklyReports").then((module) => ({ default: module.WeeklyReports }))
);
const HighestStore = lazy(() =>
  import("./pages/HighestStore").then((module) => ({ default: module.HighestStore }))
);
const PriceComparison = lazy(() =>
  import("./pages/PriceComparison").then((module) => ({ default: module.PriceComparison }))
);
const PriceComparisonReport = lazy(() =>
  import("./pages/PriceComparisonReport").then((module) => ({
    default: module.PriceComparisonReport,
  }))
);
const Innovation = lazy(() =>
  import("./pages/Innovation").then((module) => ({ default: module.Innovation }))
);
const CustomerInsights = lazy(() =>
  import("./pages/CustomerInsights").then((module) => ({ default: module.CustomerInsights }))
);
const DealAnalytics = lazy(() =>
  import("./pages/DealAnalytics").then((module) => ({ default: module.DealAnalytics }))
);
const InventoryForecasting = lazy(() =>
  import("./pages/InventoryForecasting").then((module) => ({
    default: module.InventoryForecasting,
  }))
);
const PricingIntelligence = lazy(() =>
  import("./pages/PricingIntelligence").then((module) => ({ default: module.PricingIntelligence }))
);
const EconomicDashboard = lazy(() =>
  import("./pages/EconomicDashboard").then((module) => ({ default: module.EconomicDashboard }))
);
const ProductLifecycle = lazy(() =>
  import("./pages/ProductLifecycle").then((module) => ({ default: module.ProductLifecycle }))
);
const CustomerRetention = lazy(() =>
  import("./pages/CustomerRetention").then((module) => ({ default: module.CustomerRetention }))
);
const StaffingOptimization = lazy(() =>
  import("./pages/StaffingOptimization").then((module) => ({
    default: module.StaffingOptimization,
  }))
);
const Login = lazy(() => import("./pages/Login").then((module) => ({ default: module.Login })));
const SetupAdmin = lazy(() =>
  import("./pages/SetupAdmin").then((module) => ({ default: module.SetupAdmin }))
);
const UserManagement = lazy(() =>
  import("./pages/UserManagement").then((module) => ({ default: module.UserManagement }))
);
const Profile = lazy(() =>
  import("./pages/Profile").then((module) => ({ default: module.Profile }))
);
const MerchInventoryAnalysis = lazy(() =>
  import("./pages/MerchInventoryAnalysis").then((module) => ({
    default: module.MerchInventoryAnalysis,
  }))
);
const GooglePresence = lazy(() =>
  import("./pages/GooglePresence").then((module) => ({ default: module.GooglePresence }))
);
const ApiHealth = lazy(() =>
  import("./pages/ApiHealth").then((module) => ({ default: module.ApiHealth }))
);
const DealPerformance = lazy(() =>
  import("./pages/DealPerformance").then((module) => ({ default: module.DealPerformance }))
);
const OpsActivity = lazy(() =>
  import("./pages/OpsActivity").then((module) => ({ default: module.OpsActivity }))
);
const StoreExecution = lazy(() =>
  import("./pages/StoreExecution").then((module) => ({ default: module.StoreExecution }))
);
const Tasks = lazy(() => import("./pages/Tasks").then((module) => ({ default: module.Tasks })));

const NAV_GROUPS_STORAGE_KEY = "opsConsole.navGroupsExpanded";

const DEFAULT_NAV_GROUPS_EXPANDED = {
  tasks: true,
  reports: false,
  creative: false,
};

function readNavGroupsExpanded() {
  try {
    const raw = window.localStorage.getItem(NAV_GROUPS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        ...DEFAULT_NAV_GROUPS_EXPANDED,
        ...(typeof parsed === "object" && parsed !== null ? parsed : {}),
      };
    }
  } catch {
    // Ignore storage failures.
  }
  return { ...DEFAULT_NAV_GROUPS_EXPANDED };
}

function LegacyTaskRedirect() {
  const location = useLocation();
  const tab = LEGACY_TASK_PATHS[location.pathname];
  const params = new URLSearchParams(location.search);
  if (tab) {
    params.set("tab", tab);
  }
  const query = params.toString();
  return <Navigate to={query ? `/tasks?${query}` : "/tasks"} replace />;
}

function App() {
  return (
    <Router>
      <OverflowTooltipProvider>
        <AppLayout />
      </OverflowTooltipProvider>
    </Router>
  );
}

const SIDEBAR_COLLAPSED_STORAGE_KEY = "opsConsole.sidebarCollapsed";

function readSidebarCollapsedPreference() {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, userRole, loading: authLoading, ROLES } = useAuth();
  const { logoUrl } = useLogo();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsedPreference);
  const [navGroupsExpanded, setNavGroupsExpanded] = useState(readNavGroupsExpanded);
  useSessionTimeout();

  const canViewTasks = userRole === ROLES.ADMIN || userRole === ROLES.BUYER;
  const taskCounts = useTaskHubCounts(
    currentUser?.uid || null,
    Boolean(currentUser?.uid) && canViewTasks
  );

  const isTasksRoute = location.pathname === "/tasks";
  const activeTaskTab = normalizeTaskHubTab(new URLSearchParams(location.search).get("tab"));
  const isTaskTabActive = useCallback(
    (tab) => isTasksRoute && activeTaskTab === tab,
    [isTasksRoute, activeTaskTab]
  );

  const reportPaths = useMemo(
    () =>
      new Set([
        "/daily-sales",
        "/weekly-reports",
        "/highest-store",
        "/restocking-reports",
        "/merch-inventory-analysis",
        "/inventory",
        "/floor-exposure",
      ]),
    []
  );
  const creativePaths = useMemo(
    () =>
      new Set([
        "/deal-posters",
        "/flowhub-deal-graphics",
        "/flowhub-brand-images",
        "/brand-logos",
        "/brand-asset-review",
      ]),
    []
  );
  const isReportsRoute = reportPaths.has(location.pathname);
  const isCreativeRoute = creativePaths.has(location.pathname);

  const toggleNavGroup = useCallback((group) => {
    setNavGroupsExpanded((prev) => {
      const next = { ...prev, [group]: !prev[group] };
      try {
        window.localStorage.setItem(NAV_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore storage failures.
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setNavGroupsExpanded((prev) => {
      const next = { ...prev };
      if (isTasksRoute) {
        next.tasks = true;
      }
      if (isReportsRoute) {
        next.reports = true;
      }
      if (isCreativeRoute) {
        next.creative = true;
      }
      if (
        next.tasks === prev.tasks &&
        next.reports === prev.reports &&
        next.creative === prev.creative
      ) {
        return prev;
      }
      try {
        window.localStorage.setItem(NAV_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore storage failures.
      }
      return next;
    });
  }, [isTasksRoute, isReportsRoute, isCreativeRoute]);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        // Ignore storage failures (private mode / quota).
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key.toLowerCase() !== "m" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target?.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      toggleSidebarCollapsed();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebarCollapsed]);

  // Memoize path mapping to avoid recreating on every render
  // MUST be called before any conditional returns (React Hooks rules)
  const pathMap = useMemo(
    () => ({
      Dashboard: "/",
      "Command Center": "/command-center",
      "Ops Activity": "/ops-activity",
      "Store Execution": "/store-execution",
      "4:20 Report": "/daily-sales",
      "Weekly Reports": "/weekly-reports",
      "Highest Store": "/highest-store",
      "Price Comparison": "/price-comparison",
      "Price Comparison Report": "/price-comparison-report",
      "Pricing Intelligence": "/pricing-intelligence",
      "Economic Dashboard": "/economic-dashboard",
      "Product Lifecycle": "/product-lifecycle",
      "Customer Retention": "/customer-retention",
      "Expiring Inventory": "/inventory",
      "Floor Exposure": "/floor-exposure",
      "Restocking Reports": "/restocking-reports",
      "Merch Inventory Analysis": "/merch-inventory-analysis",
      "Inventory Forecasting": "/inventory-forecasting",
      "Staffing Optimization": "/staffing-optimization",
      "Supply Orders": "/supply-orders",
      "Deal Posters": "/deal-posters",
      "Flowhub Deal Graphics": "/flowhub-deal-graphics",
      "Flowhub Brand Images": "/flowhub-brand-images",
      "Brand Logos": "/brand-logos",
      "Brand Asset Review": "/brand-asset-review",
      Tasks: "/tasks",
      "Deal Analytics": "/deal-analytics",
      "Deal Performance": "/deal-performance",
      "Google Presence": "/google-presence",
      Innovation: "/innovation",
      "Customer Insights": "/customer-insights",
      "User Management": "/user-management",
      Settings: "/settings",
      "API Health": "/api-health",
      Profile: "/profile",
    }),
    []
  );

  const getPathForLabel = useCallback(
    (label) => {
      return pathMap[label] || "/";
    },
    [pathMap]
  );

  const isActive = useCallback(
    (label) => {
      const path = getPathForLabel(label);
      return location.pathname === path || (path === "/" && location.pathname === "/");
    },
    [location.pathname, getPathForLabel]
  );

  const handleNavigate = useCallback(
    (path) => {
      logDebug("[AppLayout] Navigating to", path);
      navigate(path);
    },
    [navigate]
  );

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      logError("Logout error", error);
    }
  }, [logout, navigate]);

  // Close sidebar when route changes (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  logDebug("[AppLayout] Current location", location.pathname);

  // Don't show sidebar on login page, but redirect if already logged in
  if (location.pathname === "/login") {
    // Wait for auth to finish loading before checking
    if (authLoading) {
      return <LoadingSpinner fullScreen message="Loading..." />;
    }
    // If user is already logged in, redirect to dashboard
    if (currentUser) {
      return <Navigate to="/" replace />;
    }
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen message="Loading login..." />}>
              <Login />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className={`app-root ${sidebarCollapsed ? "app-root--sidebar-collapsed" : ""}`}>
      {/* Mobile menu button */}
      <button
        className="mobile-menu-button"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle navigation menu"
        aria-expanded={sidebarOpen}
      >
        <span className={`hamburger ${sidebarOpen ? "hamburger--open" : ""}`}>
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      {/* Desktop: restore selection menu when hidden */}
      {sidebarCollapsed && (
        <button
          type="button"
          className="sidebar-show-button"
          onClick={toggleSidebarCollapsed}
          aria-label={
            canViewTasks && taskCounts.total > 0
              ? `Show selection menu, ${taskCounts.total} open tasks`
              : "Show selection menu"
          }
          aria-expanded={false}
          aria-keyshortcuts="m"
          title={
            canViewTasks && taskCounts.total > 0
              ? `Show selection menu (${taskCounts.total} open tasks)`
              : "Show selection menu"
          }
        >
          <span className="sidebar-show-button__icon" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </span>
          <span className="sidebar-show-button__label">Menu</span>
          {canViewTasks && taskCounts.total > 0 && (
            <span className="sidebar-show-button__badge nav-badge" aria-hidden="true">
              {taskCounts.total}
            </span>
          )}
        </button>
      )}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarOpen ? "sidebar--open" : ""} ${
          sidebarCollapsed ? "sidebar--collapsed" : ""
        }`}
        aria-hidden={sidebarCollapsed ? true : undefined}
      >
        <div className="sidebar-header">
          <div className="sidebar-header-brand">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Meds Cafe"
                style={{
                  width: "48px",
                  height: "48px",
                  objectFit: "contain",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                }}
              />
            ) : (
              <div className="logo-circle">MC</div>
            )}
            <div>
              <div className="sidebar-title">Meds Cafe</div>
              <div className="sidebar-subtitle">Ops Console</div>
            </div>
          </div>
          <button
            type="button"
            className="sidebar-hide-button"
            onClick={toggleSidebarCollapsed}
            aria-label="Hide selection menu"
            title="Hide selection menu"
          >
            <span aria-hidden="true">«</span>
          </button>
        </div>

        <nav className="nav" aria-label="Selection menu">
          <div className="nav-section-header">MANAGEMENT</div>
          <NavItem
            label="Dashboard"
            active={isActive("Dashboard")}
            onClick={() => handleNavigate("/")}
          />

          <NavItem
            label="Command Center"
            active={isActive("Command Center")}
            onClick={() => handleNavigate("/command-center")}
          />

          <NavItem
            label="Supply Orders"
            active={isActive("Supply Orders")}
            onClick={() => handleNavigate("/supply-orders")}
          />

          {(userRole === ROLES.ADMIN ||
            userRole === ROLES.BUYER ||
            userRole === ROLES.STORE_MANAGER) && (
            <NavItem
              label="Store Execution"
              active={isActive("Store Execution")}
              onClick={() => handleNavigate("/store-execution")}
            />
          )}

          {canViewTasks && (
            <>
              <NavGroupButton
                label="Tasks"
                badge={taskCounts.total > 0 ? taskCounts.total : null}
                expanded={navGroupsExpanded.tasks}
                active={isTasksRoute}
                onToggle={() => toggleNavGroup("tasks")}
              />
              {navGroupsExpanded.tasks && (
                <div className="nav-group-children">
                  <NavItem
                    label="All queues"
                    active={isTaskTabActive(TASK_HUB_TABS.ALL)}
                    onClick={() => handleNavigate("/tasks")}
                  />
                  <NavItem
                    label="Buyer"
                    badge={taskCounts.buyer > 0 ? taskCounts.buyer : null}
                    active={isTaskTabActive(TASK_HUB_TABS.BUYER)}
                    onClick={() => handleNavigate(taskHubPath(TASK_HUB_TABS.BUYER))}
                  />
                  <NavItem
                    label="Menu Gap"
                    badge={taskCounts.menuGap > 0 ? taskCounts.menuGap : null}
                    active={isTaskTabActive(TASK_HUB_TABS.MENU_GAP)}
                    onClick={() => handleNavigate(taskHubPath(TASK_HUB_TABS.MENU_GAP))}
                  />
                  <NavItem
                    label="Inventory Cleanup"
                    badge={taskCounts.cleanup > 0 ? taskCounts.cleanup : null}
                    active={isTaskTabActive(TASK_HUB_TABS.CLEANUP)}
                    onClick={() => handleNavigate(taskHubPath(TASK_HUB_TABS.CLEANUP))}
                  />
                  <NavItem
                    label="Stale Inventory"
                    badge={taskCounts.stale > 0 ? taskCounts.stale : null}
                    active={isTaskTabActive(TASK_HUB_TABS.STALE)}
                    onClick={() => handleNavigate(taskHubPath(TASK_HUB_TABS.STALE))}
                  />
                </div>
              )}
            </>
          )}

          <NavGroupButton
            label="Reports"
            expanded={navGroupsExpanded.reports}
            active={isReportsRoute}
            onToggle={() => toggleNavGroup("reports")}
          />
          {navGroupsExpanded.reports && (
            <div className="nav-group-children">
              <NavItem
                label="4:20 Report"
                active={isActive("4:20 Report")}
                onClick={() => handleNavigate("/daily-sales")}
              />
              {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
                <NavItem
                  label="Weekly Reports"
                  active={isActive("Weekly Reports")}
                  onClick={() => handleNavigate("/weekly-reports")}
                />
              )}
              {userRole === ROLES.ADMIN && (
                <NavItem
                  label="Highest Store"
                  active={isActive("Highest Store")}
                  onClick={() => handleNavigate("/highest-store")}
                />
              )}
              {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
                <NavItem
                  label="Restocking Reports"
                  active={isActive("Restocking Reports")}
                  onClick={() => handleNavigate("/restocking-reports")}
                />
              )}
              {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
                <NavItem
                  label="Merch Inventory Analysis"
                  active={isActive("Merch Inventory Analysis")}
                  onClick={() => handleNavigate("/merch-inventory-analysis")}
                />
              )}
              <NavItem
                label="Expiring Inventory"
                active={isActive("Expiring Inventory")}
                onClick={() => handleNavigate("/inventory")}
              />
              <NavItem
                label="Floor Exposure"
                active={isActive("Floor Exposure")}
                onClick={() => handleNavigate("/floor-exposure")}
              />
            </div>
          )}

          {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
            <>
              <NavGroupButton
                label="Creative"
                expanded={navGroupsExpanded.creative}
                active={isCreativeRoute}
                onToggle={() => toggleNavGroup("creative")}
              />
              {navGroupsExpanded.creative && (
                <div className="nav-group-children">
                  {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
                    <NavItem
                      label="Deal Posters"
                      active={isActive("Deal Posters")}
                      onClick={() => handleNavigate("/deal-posters")}
                    />
                  )}
                  {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
                    <NavItem
                      label="Flowhub Deal Graphics"
                      active={isActive("Flowhub Deal Graphics")}
                      onClick={() => handleNavigate("/flowhub-deal-graphics")}
                    />
                  )}
                  {userRole === ROLES.ADMIN && (
                    <>
                      <NavItem
                        label="Flowhub Brand Images"
                        active={isActive("Flowhub Brand Images")}
                        onClick={() => handleNavigate("/flowhub-brand-images")}
                      />
                      <NavItem
                        label="Brand Logos"
                        active={isActive("Brand Logos")}
                        onClick={() => handleNavigate("/brand-logos")}
                      />
                      <NavItem
                        label="Brand Asset Review"
                        active={isActive("Brand Asset Review")}
                        onClick={() => handleNavigate("/brand-asset-review")}
                      />
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
            <NavItem
              label="Ops Activity"
              active={isActive("Ops Activity")}
              onClick={() => handleNavigate("/ops-activity")}
            />
          )}

          <div className="nav-section-header">TESTING</div>
          <NavItem
            label="Price Comparison"
            active={isActive("Price Comparison")}
            onClick={() => handleNavigate("/price-comparison")}
          />
          <NavItem
            label="Price Comparison Report"
            active={isActive("Price Comparison Report")}
            onClick={() => handleNavigate("/price-comparison-report")}
          />
          {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
            <NavItem
              label="Deal Performance"
              active={isActive("Deal Performance")}
              onClick={() => handleNavigate("/deal-performance")}
            />
          )}
          {(userRole === ROLES.ADMIN || userRole === ROLES.BUYER) && (
            <NavItem
              label="Google Presence"
              active={isActive("Google Presence")}
              onClick={() => handleNavigate("/google-presence")}
            />
          )}
          <NavItem
            label="Innovation"
            active={isActive("Innovation")}
            onClick={() => handleNavigate("/innovation")}
          />

          <div className="nav-section-header">ADMIN</div>
          {userRole === ROLES.ADMIN && (
            <>
              <NavItem
                label="User Management"
                active={isActive("User Management")}
                onClick={() => handleNavigate("/user-management")}
              />
              <NavItem
                label="Settings"
                active={isActive("Settings")}
                onClick={() => handleNavigate("/settings")}
              />
              <NavItem
                label="API Health"
                active={isActive("API Health")}
                onClick={() => handleNavigate("/api-health")}
              />
            </>
          )}
          <NavItem
            label="Profile"
            active={isActive("Profile")}
            onClick={() => handleNavigate("/profile")}
          />
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user-info">
            <div className="sidebar-user-email">{currentUser?.email || "Not signed in"}</div>
            <div className="sidebar-user-role">{userRole || "No role"}</div>
          </div>
          <button className="sidebar-logout-button" onClick={handleLogout}>
            Sign Out
          </button>
          <div className="sidebar-footer-label">Environment</div>
          <div className="sidebar-chip">Production</div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main">
        <Routes>
          <Route
            path="/login"
            element={
              <Suspense fallback={<LoadingSpinner fullScreen message="Loading login..." />}>
                <Login />
              </Suspense>
            }
          />
          <Route
            path="/setup-admin"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Admin Setup">
                  <Suspense fallback={<LoadingSpinner fullScreen message="Loading..." />}>
                    <SetupAdmin />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Dashboard">
                  <Suspense fallback={<LoadingSpinner fullScreen message="Loading dashboard..." />}>
                    <Dashboard />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/command-center"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Command Center">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading Command Center..." />}
                  >
                    <CommandCenter />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ops-activity"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Ops Activity">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading ops activity..." />}
                  >
                    <OpsActivity />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/store-execution"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER, ROLES.STORE_MANAGER]}>
                <SectionErrorBoundary sectionName="Store Execution">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading store execution..." />}
                  >
                    <StoreExecution />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requiredRole={ROLES.ADMIN}>
                <SectionErrorBoundary sectionName="Settings">
                  <Suspense fallback={<LoadingSpinner fullScreen message="Loading settings..." />}>
                    <Settings />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings-test"
            element={
              <ProtectedRoute requiredRole={ROLES.ADMIN}>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading..." />}>
                  <SettingsTest />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-sales"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Daily Sales">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading daily sales..." />}
                  >
                    <DailySales />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/weekly-reports"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Weekly Reports">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading weekly reports..." />}
                  >
                    <WeeklyReports />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/highest-store"
            element={
              <ProtectedRoute requiredRole={ROLES.ADMIN}>
                <SectionErrorBoundary sectionName="Highest Store">
                  <Suspense fallback={<LoadingSpinner fullScreen message="Loading..." />}>
                    <HighestStore />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/price-comparison"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Price Comparison">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading price comparison..." />}
                  >
                    <PriceComparison />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/price-comparison-report"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Price Comparison Report">
                  <Suspense
                    fallback={
                      <LoadingSpinner fullScreen message="Loading price comparison report..." />
                    }
                  >
                    <PriceComparisonReport />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing-intelligence"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Pricing Intelligence">
                  <Suspense
                    fallback={
                      <LoadingSpinner fullScreen message="Loading pricing intelligence..." />
                    }
                  >
                    <PricingIntelligence />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/economic-dashboard"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Economic Dashboard">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading economic dashboard..." />}
                  >
                    <EconomicDashboard />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/product-lifecycle"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Product Lifecycle">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading product lifecycle..." />}
                  >
                    <ProductLifecycle />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-retention"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Customer Retention">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading customer retention..." />}
                  >
                    <CustomerRetention />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Inventory">
                  <Suspense fallback={<LoadingSpinner fullScreen message="Loading inventory..." />}>
                    <Inventory />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/floor-exposure"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Floor Exposure">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading floor exposure..." />}
                  >
                    <FloorExposure />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/restocking-reports"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Restocking Reports">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading restocking reports..." />}
                  >
                    <RestockingReports />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/merch-inventory-analysis"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Merch Inventory Analysis">
                  <Suspense
                    fallback={
                      <LoadingSpinner fullScreen message="Loading merch inventory analysis..." />
                    }
                  >
                    <MerchInventoryAnalysis />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-forecasting"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Inventory Forecasting">
                  <Suspense
                    fallback={
                      <LoadingSpinner fullScreen message="Loading inventory forecasting..." />
                    }
                  >
                    <InventoryForecasting />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/staffing-optimization"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Staffing Optimization">
                  <Suspense
                    fallback={
                      <LoadingSpinner fullScreen message="Loading staffing optimization..." />
                    }
                  >
                    <StaffingOptimization />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/supply-orders"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Supply Orders">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading supply orders..." />}
                  >
                    <SupplyOrders />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deal-posters"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Deal Posters">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading deal posters..." />}
                  >
                    <DealPosters />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/flowhub-deal-graphics"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Flowhub Deal Graphics">
                  <Suspense
                    fallback={
                      <LoadingSpinner fullScreen message="Loading Flowhub deal graphics..." />
                    }
                  >
                    <FlowhubDealGraphics />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/flowhub-brand-images"
            element={
              <ProtectedRoute requiredRole={ROLES.ADMIN}>
                <SectionErrorBoundary sectionName="Flowhub Brand Images">
                  <Suspense
                    fallback={
                      <LoadingSpinner fullScreen message="Loading Flowhub brand images..." />
                    }
                  >
                    <FlowhubBrandImages />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-logos"
            element={
              <ProtectedRoute requiredRole={ROLES.ADMIN}>
                <SectionErrorBoundary sectionName="Brand Logos">
                  <Suspense fallback={<LoadingSpinner fullScreen message="Loading brand logos..." />}>
                    <BrandLogos />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-asset-review"
            element={
              <ProtectedRoute requiredRole={ROLES.ADMIN}>
                <SectionErrorBoundary sectionName="Brand Asset Review">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading brand asset review..." />}
                  >
                    <BrandAssetReview />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Tasks">
                  <Suspense fallback={<LoadingSpinner fullScreen message="Loading tasks..." />}>
                    <Tasks />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="/buyer-tasks" element={<LegacyTaskRedirect />} />
          <Route path="/inventory-cleanup-tasks" element={<LegacyTaskRedirect />} />
          <Route path="/stale-inventory-tasks" element={<LegacyTaskRedirect />} />
          <Route path="/menu-gap-tasks" element={<LegacyTaskRedirect />} />
          <Route
            path="/deal-analytics"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Deal Analytics">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading deal analytics..." />}
                  >
                    <DealAnalytics />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deal-performance"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <DealPerformance />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/google-presence"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Google Presence">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading Google presence..." />}
                  >
                    <GooglePresence />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/innovation"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Innovation Lab">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading innovation lab..." />}
                  >
                    <Innovation />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-insights"
            element={
              <ProtectedRoute requiredRole={[ROLES.ADMIN, ROLES.BUYER]}>
                <SectionErrorBoundary sectionName="Customer Insights">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading customer insights..." />}
                  >
                    <CustomerInsights />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute requiredRole={ROLES.ADMIN}>
                <SectionErrorBoundary sectionName="User Management">
                  <Suspense
                    fallback={<LoadingSpinner fullScreen message="Loading user management..." />}
                  >
                    <UserManagement />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/api-health"
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<LoadingSpinner />}>
                  <ApiHealth />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <SectionErrorBoundary sectionName="Profile">
                  <Suspense fallback={<LoadingSpinner fullScreen message="Loading profile..." />}>
                    <Profile />
                  </Suspense>
                </SectionErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<div className="page-placeholder">Page not found</div>} />
        </Routes>
      </main>
    </div>
  );
}

const NavItem = React.memo(({ label, badge, active, onClick }) => {
  const handleClick = useCallback(
    (e) => {
      logDebug(`[NavItem] Clicked`, label);
      e.preventDefault();
      e.stopPropagation();
      if (onClick) {
        onClick();
      }
    },
    [label, onClick]
  );

  return (
    <div
      className={`nav-item ${active ? "nav-item--active" : ""}`}
      onClick={handleClick}
      onMouseDown={(e) => e.preventDefault()} // Prevent any default behavior
      style={{ cursor: "pointer", userSelect: "none" }}
      role="button"
      tabIndex={0}
      aria-label={`Navigate to ${label}`}
      aria-current={active ? "page" : undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      <span>
        {label}
        {Number(badge) > 0 ? <span className="nav-badge">{badge}</span> : null}
      </span>
    </div>
  );
});

const NavGroupButton = React.memo(({ label, badge, expanded, active, onToggle }) => {
  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    },
    [onToggle]
  );

  return (
    <button
      type="button"
      className={`nav-group-btn${active ? " nav-group-btn--active" : ""}`}
      onClick={handleClick}
      aria-expanded={expanded}
      aria-label={`Toggle ${label} menu`}
    >
      <span>
        {label}
        {Number(badge) > 0 ? <span className="nav-badge">{badge}</span> : null}
      </span>
      <span className="nav-group-btn__chev" aria-hidden="true">
        {expanded ? "▾" : "▸"}
      </span>
    </button>
  );
});

export default App;
